import React from "react";
import { Section, Reveal, Icon, Button } from "../components/primitives.jsx";
import { IconTile } from "../components/blocks.jsx";
import { PageHero, HeroArt } from "../components/visuals.jsx";

export function Contact({ c, radius = 16 }) {
  const t = c.contactPage;
  const f = t.form;

  const [vals, setVals] = React.useState({ name: "", email: "", company: "", interest: f.interestOptions[0], message: "" });
  const [errs, setErrs] = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [status, setStatus] = React.useState("idle"); // idle | sending | sent

  const validate = (v) => {
    const e = {};
    if (!v.name.trim()) e.name = f.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = f.errEmail;
    if (v.message.trim().length < 4) e.message = f.errMessage;
    return e;
  };

  const set = (k, val) => {
    const next = { ...vals, [k]: val };
    setVals(next);
    if (touched[k]) setErrs(validate(next));
  };
  const blur = (k) => {
    setTouched({ ...touched, [k]: true });
    setErrs(validate(vals));
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate(vals);
    setErrs(e);
    setTouched({ name: true, email: true, message: true });
    if (Object.keys(e).length) return;
    setStatus("sending");
    setTimeout(() => setStatus("sent"), 900);
  };

  const fieldStyle = (k) => ({
    width: "100%",
    boxSizing: "border-box",
    height: "var(--control-h-md)",
    padding: "0 14px",
    borderRadius: Math.max(8, radius - 4),
    border: `1.5px solid ${errs[k] ? "var(--color-danger)" : "var(--border-default)"}`,
    background: "var(--surface-card)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--text-sm)",
    color: "var(--brand-ink)",
    outline: "none",
    transition: "border-color var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)"
  });
  const labelStyle = { display: "block", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-strong)", marginBottom: 7 };
  const errStyle = { display: "block", fontSize: "var(--text-xs)", color: "var(--color-danger)", marginTop: 6 };
  const onFocus = (e, k) => { if (!errs[k]) { e.target.style.borderColor = "var(--brand-red)"; e.target.style.boxShadow = "0 0 0 4px var(--red-50)"; } };
  const offFocus = (e, k) => { e.target.style.boxShadow = "none"; if (!errs[k]) e.target.style.borderColor = "var(--border-default)"; };

  // Render helper (not a nested component) so inputs keep focus across keystrokes.
  const field = (k, type = "text", full = false) => (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <label style={labelStyle} htmlFor={k}>{f[k]}</label>
      <input
        id={k}
        type={type}
        value={vals[k]}
        placeholder={k === "email" ? "you@company.com" : ""}
        onChange={(e) => set(k, e.target.value)}
        onBlur={(e) => { blur(k); offFocus(e, k); }}
        onFocus={(e) => onFocus(e, k)}
        style={fieldStyle(k)}
      />
      {errs[k] && <span style={errStyle}>{errs[k]}</span>}
    </div>
  );

  return (
    <main style={{ fontFamily: "var(--font-sans)" }}>
      <PageHero eyebrow={t.eyebrow} title={t.title} lede={t.lede} radius={radius} art={<HeroArt kind="contact" radius={radius} />} />

      <Section tone="wash">
        <div className="rw-contact" style={{ display: "grid", gridTemplateColumns: "1fr 1.25fr", gap: "var(--space-9)", alignItems: "start" }}>
          <Reveal>
            <div style={{ fontSize: "var(--text-xs)", fontWeight: 700, letterSpacing: "var(--tracking-widest)", textTransform: "uppercase", color: "var(--brand-red)" }}>{t.infoTitle}</div>
            <div style={{ marginTop: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              {t.info.map((it) => (
                <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <IconTile name={it.icon} tone="ink" radius={Math.max(8, radius - 4)} size={44} />
                  <div>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{it.label}</div>
                    {it.href ? (
                      <a
                        href={it.href}
                        target={it.external ? "_blank" : undefined}
                        rel={it.external ? "noreferrer" : undefined}
                        style={{ display: "inline-block", fontSize: "var(--text-md)", fontWeight: 700, lineHeight: 1.35, color: "var(--brand-ink)", overflowWrap: "anywhere" }}
                      >
                        {it.value}
                      </a>
                    ) : (
                      <div style={{ fontSize: "var(--text-md)", fontWeight: 700, color: "var(--brand-ink)" }}>{it.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "var(--space-7)", padding: "var(--space-5)", borderRadius: radius, background: "var(--brand-ink)", color: "#fff" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 600, color: "var(--neutral-0)" }}>{c.footer.tagline}</div>
              <p style={{ margin: "10px 0 0", fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--neutral-400)" }}>{c.footer.blurb}</p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <form onSubmit={submit} noValidate style={{ background: "var(--surface-card)", borderRadius: radius + 4, padding: "clamp(1.5rem, 3vw, var(--space-8))", border: "1px solid var(--border-subtle)", boxShadow: "var(--shadow-md)" }}>
              {status === "sent" ? (
                <div style={{ textAlign: "center", padding: "var(--space-8) var(--space-4)" }}>
                  <span style={{ display: "inline-flex", width: 64, height: 64, borderRadius: 999, background: "var(--color-success-surface)", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="check" size={30} color="var(--color-success)" strokeWidth={2.6} />
                  </span>
                  <h3 style={{ margin: "20px 0 0", fontSize: "var(--text-xl)", fontWeight: 800, letterSpacing: "-0.02em", color: "var(--brand-ink)" }}>{f.success}</h3>
                </div>
              ) : (
                <React.Fragment>
                  <div className="rw-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-5)" }}>
                    {field("name")}
                    {field("email", "email")}
                    {field("company")}
                    <div>
                      <label style={labelStyle} htmlFor="interest">{f.interest}</label>
                      <select
                        id="interest"
                        value={vals.interest}
                        onChange={(e) => set("interest", e.target.value)}
                        style={{ ...fieldStyle("interest"), appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B6B73' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 38, cursor: "pointer" }}
                      >
                        {f.interestOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelStyle} htmlFor="message">{f.message}</label>
                      <textarea
                        id="message"
                        rows={5}
                        value={vals.message}
                        onChange={(e) => set("message", e.target.value)}
                        onBlur={(e) => { blur("message"); offFocus(e, "message"); }}
                        onFocus={(e) => onFocus(e, "message")}
                        style={{ ...fieldStyle("message"), height: "auto", padding: "12px 14px", resize: "vertical", lineHeight: 1.6 }}
                      />
                      {errs.message && <span style={errStyle}>{errs.message}</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: "var(--space-6)" }}>
                    <Button type="submit" variant="primary" size="lg" disabled={status === "sending"} style={{ width: "100%" }} iconRight={status === "sending" ? null : <Icon name="send" size={17} color="#fff" />}>
                      {status === "sending" ? f.sending : f.submit}
                    </Button>
                  </div>
                </React.Fragment>
              )}
            </form>
          </Reveal>
        </div>
      </Section>
    </main>
  );
}
